import { FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../contexts/AppContext';
import Header from '../framework/Header';
import ErrorMessageView from '../components/ErrorMessage';
import useFetchDocument from '../hooks/useFetchDocument';
import { usePlausiblePageview } from '../hooks/usePlausibleTracker';
import { getTranslation } from '../common/Translation';
import UrlIcon from '../assets/item_url.svg?react';

/** Sandbox for the external page */
const IFRAME_SANDBOX = "allow-scripts allow-same-origin";

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  overflow: hidden;
  /* No transform here: it would make the fixed-position menu in Header position relative to this wrapper */

  /* Kept-alive pages stay loaded in the background, but must not be seen or receive input */
  &.inactive {
    visibility: hidden;
    pointer-events: none;
  }

  &.active {
    z-index: 10;
  }
`;

const PreviewPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px;
  box-sizing: border-box;
  text-align: center;
  color: ${p => p.theme?.textColor || "#fff"};

  & svg {
    width: 64px;
    height: 64px;
    fill: currentColor;
    opacity: 0.6;
  }

  & p {
    margin: 0;
    max-width: 600px;
    font-size: 1.1em;
  }

  & small {
    opacity: 0.6;
    word-break: break-all;
  }
`;

const Frame = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`;

interface UrlSettings {
  url?: string,
  leaveOpen?: boolean,
  restrictDomains?: boolean,
  allowedDomains?: string[],
  hideHeader?: boolean,
  disallowScroll?: boolean,
  hideInPreview?: boolean
}

interface UrlViewProps {
  /** Presentation id. Falls back to the route param */
  documentId?: string,
  /** Whether this view is the one currently shown. Defaults to true */
  active?: boolean,
  previewData?: any,
  overrideTitle?: string,
  /** Reports whether the presentation wants to be kept alive after leaving it */
  onLeaveOpen?: (documentId: string, leaveOpen: boolean) => void
}

/**
 * Hostname of a URL, or null if the URL is invalid
 */
const getHost = (url?: string): string | null => {
  try {
    return url ? new URL(url).hostname : null;
  } catch {
    return null;
  }
};

/**
 * Normalize a domain from the CMS. Accepts both "abc.com" and "https://abc.com/path" (→ "abc.com").
 * A leading "*." is kept, so subdomains can be allowed explicitly ("*.abc.com", "https://*.abc.com").
 */
const normalizeDomain = (domain: string): string | null => {
  const withoutScheme = String(domain || "").trim().toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
  if (!withoutScheme) { return null; }
  const wildcard = withoutScheme.startsWith("*.");
  const bare = wildcard ? withoutScheme.slice(2) : withoutScheme;
  const host = getHost(`https://${bare}`);
  // Only allow plain hostnames, so nothing can break out of the CSP string
  if (!host || !/^[a-z0-9.-]+$/.test(host)) { return null; }
  return wildcard ? `*.${host}` : host;
};

/**
 * Build a frame-src source list for the URL's host and allowed domains.
 * Only the exact hosts are allowed (plus the www/non-www variant), not other subdomains.
 */
const buildFrameSources = (url: string, allowedDomains?: string[]): string => {
  const hosts = new Set<string>();
  [getHost(url), ...(allowedDomains || [])].forEach(domain => {
    const host = domain && normalizeDomain(domain);
    if (!host) { return; }
    hosts.add(host);
    if (!host.startsWith("*.")) {
      hosts.add(host.startsWith("www.") ? host.slice(4) : `www.${host}`);
    }
  });
  return Array.from(hosts).join(" ");
};

/**
 * Escape a string for use inside a double-quoted HTML attribute
 */
const escapeAttribute = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * URL to load when re-opening a kept-alive page. "#top" makes the browser scroll to the top
 * without reloading if the page is still on the original URL, and does a full load otherwise.
 * URLs that already use a fragment (e.g. hash routing) are reloaded instead.
 */
const getResetUrl = (url: string) => (url.includes("#") ? url : `${url}#top`);

/**
 * Render an external URL in fullscreen
 * @returns {JSX.Element} Component template
 */
const UrlView: FC<UrlViewProps> = ({ documentId: documentIdProp, active = true, previewData = null, overrideTitle, onLeaveOpen }) => {
  const { t } = useTranslation();
  const params = useParams<{ documentId: string }>();
  const documentId = documentIdProp ?? params.documentId;
  const { deviceId, currentLanguage, resetTimer, previewIsActive } = useContext(AppContext);
  const [data, error] = useFetchDocument(documentId, previewData);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const wasActive = useRef<boolean>(active);

  // content.content is the older location, kept for presentations saved before the API change
  const settings: UrlSettings = data?.content?.webcontent || data?.content?.content || {};
  const url = settings?.url && getHost(settings.url) ? settings.url : undefined;
  const restrictDomains = Boolean(settings?.restrictDomains);
  const leaveOpen = Boolean(settings?.leaveOpen);
  const hideHeader = Boolean(settings?.hideHeader ?? data?.content?.hideHeader);
  // scrolling="no" stops the user from scrolling the page itself. Inner scroll areas on the page cannot be controlled cross-origin.
  const disallowScroll = Boolean(settings?.disallowScroll);
  // Heavy pages (e.g. large Sketchfab models) can freeze the CMS preview: show a placeholder instead
  const showPlaceholder = Boolean(previewIsActive && settings?.hideInPreview);

  usePlausiblePageview(
    { deviceId: String(deviceId), documentId: documentId, documentType: 'url', ownerId: data?.owner_id, ownerSlug: data?.owner_slug, language: currentLanguage },
    { waitFor: ['ownerSlug'] }
  );

  // Let the host know if this page should stay mounted after leaving it
  useEffect(() => {
    if (documentId && data?.content) {
      onLeaveOpen?.(documentId, leaveOpen);
    }
  }, [documentId, data?.content, leaveOpen, onLeaveOpen]);

  /**
   * srcdoc wrapper that enforces frame-src on the external page, including later navigations inside it
   */
  const restrictedDoc = useMemo(() => {
    if (!url || !restrictDomains) { return undefined; }
    const csp = `frame-src ${buildFrameSources(url, settings?.allowedDomains)}`;
    return `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="${escapeAttribute(csp)}">`
      + `<style>html,body{margin:0;height:100%;overflow:hidden;background:#000}iframe{border:0;width:100%;height:100%;display:block}</style></head>`
      + `<body><iframe src="${escapeAttribute(url)}" sandbox="${IFRAME_SANDBOX}"${disallowScroll ? ` scrolling="no"` : ""}></iframe></body></html>`;
  }, [url, restrictDomains, settings?.allowedDomains, disallowScroll]);

  /**
   * The iframe showing the external page (the inner one when domains are restricted)
   */
  const getContentFrame = useCallback((): HTMLIFrameElement | null => {
    if (!restrictedDoc) { return frameRef.current; }
    return frameRef.current?.contentDocument?.querySelector("iframe") || null;
  }, [restrictedDoc]);

  /**
   * Load the original URL again
   */
  const resetFrame = useCallback((targetUrl: string) => {
    getContentFrame()?.setAttribute("src", targetUrl);
  }, [getContentFrame]);

  // Kept-alive page is shown again: go back to the original URL and scroll to top
  useEffect(() => {
    if (active && !wasActive.current && url) {
      resetFrame(getResetUrl(url));
    }
    wasActive.current = active;
  }, [active, url, resetFrame]);

  // Navigation to a domain that is not allowed: load the original URL again
  const attachViolationListener = useCallback(() => {
    const innerDocument = frameRef.current?.contentDocument;
    if (!restrictedDoc || !innerDocument || !url) { return; }

    innerDocument.addEventListener("securitypolicyviolation", (e: SecurityPolicyViolationEvent) => {
      if (!e.effectiveDirective?.startsWith("frame-src")) { return; }
      console.warn(`Blocked navigation to ${e.blockedURI}`);
      resetFrame(url);
    });
  }, [restrictedDoc, url, resetFrame]);

  // Reset idle timer when the user interacts with the iframe
  useEffect(() => {
    if (!active || previewIsActive) { return; }

    let refocusTimer: number | undefined;
    const onBlur = () => {
      if (frameRef.current && document.activeElement === frameRef.current) {
        resetTimer();
        // Take focus back, so the next interaction triggers a new blur event
        refocusTimer = window.setTimeout(() => { window.focus(); }, 1000);
      }
    };

    window.addEventListener("blur", onBlur);
    return () => {
      window.clearTimeout(refocusTimer);
      window.removeEventListener("blur", onBlur);
    };
  }, [active, previewIsActive, resetTimer]);

  if (error) { return active ? <ErrorMessageView title={t("presentationNotFound.title")} body={t("presentationNotFound.body")} redirect={true}/> : null; }

  if (data?.status && data?.status !== "published") { return active ? <ErrorMessageView title={t("presentationNotPublished.title")} body={t("presentationNotPublished.body")} redirect={true}/> : null; }

  return (
    <Wrapper className={active ? "active" : "inactive"} aria-hidden={!active}>
      {/* Only the visible page renders a header, to avoid several menus in the DOM */}
      {active && <Header hideBar={hideHeader} title={getTranslation(overrideTitle, currentLanguage) || (data?.content?.general?.showTitle ? getTranslation(data?.content?.general?.title, currentLanguage) : "")} backgroundColor={data?.content?.background?.backgroundColor}/>}
      {url && showPlaceholder && (
        <PreviewPlaceholder>
          <UrlIcon aria-hidden/>
          <p>{t("url.hiddenInPreview")}</p>
          <small>{url}</small>
        </PreviewPlaceholder>
      )}
      {url && !showPlaceholder && (restrictedDoc
        ? <Frame key={restrictedDoc} ref={frameRef} srcDoc={restrictedDoc} onLoad={attachViolationListener} title={url}/>
        : <Frame key={`${url}-${disallowScroll}`} ref={frameRef} src={url} sandbox={IFRAME_SANDBOX} scrolling={disallowScroll ? "no" : undefined} title={url}/>
      )}
    </Wrapper>
  );
};

export default UrlView;
